import ImportExportBar from "../../finance/shared/ImportExportBar";
import { TableToolbar, ToolbarSelect } from "../../finance/shared/TableToolbar";
import RelacionesAlert from "../../../shared/components/RelacionesAlert";
import { Link, useNavigate } from "react-router-dom";
import { useState, useEffect, useCallback } from "react";
import apiFetch from "../../../shared/services/apiClient";
import styles from "../components/Tables/ListResidents.module.css";
import stepsStyles from "../components/Stepper/Steps/steps.module.css";
import { FieldError } from "../components/FieldHelpers";
import { useCedulaLookup } from "../services/useCedulaLookup";
import { useEsCliente } from "../../../shared/hooks/useTenantTipo";
import { overlayClose } from "../../../shared/utils/overlayClose";
import { useLang } from "../../../shared/i18n/useLang";


import { validatePropietario } from "../services/validationRules";
// ── Imperative toast (funciona en cualquier componente sin hook) ──────────────
function showToast(msg, ok = true) {
  const existing = document.getElementById('__condo_toast__');
  if (existing) existing.remove();
  const el = document.createElement('div');
  el.id = '__condo_toast__';
  el.style.cssText = [
    'position:fixed','bottom:24px','right:24px','z-index:9999',
    'padding:12px 20px','border-radius:10px','font-size:0.88rem',
    'font-weight:600','max-width:400px','word-break:break-word',
    'box-shadow:0 8px 32px rgba(0,0,0,0.45)',
    ok
      ? 'background:rgba(34,197,94,0.15);border:1px solid rgba(34,197,94,0.4);color:#86efac'
      : 'background:rgba(239,68,68,0.15);border:1px solid rgba(239,68,68,0.4);color:#fca5a5',
  ].join(';');
  el.textContent = msg;
  document.body.appendChild(el);
  setTimeout(() => el.remove(), 5000);
}

// ── Shared modal styles ───────────────────────────────────────────────────────
const OVERLAY = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.65)",
  backdropFilter: "blur(3px)",
  zIndex: 1000,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};
const MODAL_BOX = {
  background: "var(--bg-card)",
  border: "1px solid var(--border-card)",
  borderRadius: 12,
  padding: 24,
  zIndex: 1001,
  boxShadow: "0 24px 64px rgba(0,0,0,0.55)",
  width: "min(560px,94vw)",
  maxHeight: "90vh",
  overflowY: "auto",
};

// ── Confirm delete modal ──────────────────────────────────────────────────────
function ConfirmModal({ open, name, onConfirm, onCancel, loading }) {
  if (!open) return null;
  return (
    <div style={OVERLAY} {...overlayClose(onCancel)}>
      <div
        style={{ ...MODAL_BOX, width: "min(400px,90vw)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ textAlign: "center", marginBottom: 16 }}>
          <div style={{ fontSize: "2.5rem", marginBottom: 8 }}>🗑️</div>
          <h3 style={{ color: "#f1f5f9", margin: 0 }}>¿Eliminar registro?</h3>
          <p style={{ color: "var(--text-secondary)", marginTop: 6, fontSize: "0.88rem" }}>
            Se eliminará <strong style={{ color: "#f87171" }}>{name}</strong>{" "}
            permanentemente. Esta acción no se puede deshacer.
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
          <button
            type="button"
            onClick={onCancel}
            style={{
              padding: "8px 20px",
              background: "rgba(55,65,81,0.4)",
              border: "1px solid var(--border-card)",
              borderRadius: 7,
              color: "var(--text-secondary)",
              cursor: "pointer",
              fontSize: "0.84rem",
            }}
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={loading}
            style={{
              padding: "8px 20px",
              background: "rgba(239,68,68,0.15)",
              border: "1px solid rgba(239,68,68,0.4)",
              borderRadius: 7,
              color: "#f87171",
              cursor: "pointer",
              fontSize: "0.84rem",
              fontWeight: 600,
            }}
          >
            {loading ? "Eliminando…" : "Sí, eliminar"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Generic field row ─────────────────────────────────────────────────────────
function Field({ label, required, children, error }) {
  return (
    <div className="form-group mb-2">
      <label
        className="form-label"
        style={{ fontSize: "0.83rem", color: "var(--text-secondary)" }}
      >
        {label}
        {required && <span style={{ color: "#f87171" }}> *</span>}
      </label>
      {children}
      <FieldError msg={error} />
    </div>
  );
}

function Inp({ value, onChange, onBlur, placeholder, style, maxLength }) {
  return (
    <input
      type="text"
      className={`${stepsStyles["form-control"]} form-control`}
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      placeholder={placeholder}
      style={style}
      maxLength={maxLength}
    />
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// EDIT CLIENTE MODAL (modo cliente; en condominio se edita con el asistente)
// ═══════════════════════════════════════════════════════════════════════════════
function EditOwnerModal({ owner, onClose, onSaved }) {
  const esCliente = useEsCliente();
  const L = useLang();
  // owner: registro de /api/residents/propietario/
  const emailObj = (owner.emails || []).find((e) => e.principal) || (owner.emails || [])[0];
  const [form, setForm] = useState({
    cedula: owner.persona?.cedula || "",
    tipo_cedula: owner.persona?.tipo_cedula || "",
    idFilial: owner.filial_data?.idFilial || "",
    nombre: owner.persona?.nombre || "",
    apellidos: owner.persona?.apellidos || "",
    nombre_sociedad: owner.persona?.nombre_sociedad || "",
    telefono_casa: owner.telefono_casa || "",
    telefono_cel: owner.telefono_cel || "",
    telefono_trabajo: owner.telefono_trabajo || "",
    notas: owner.notas || "",
    emailPrincipal: emailObj?.email || "",
    emailExtras: [],
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const { buscar, resultado, estado } = useCedulaLookup();

  useEffect(() => {
    if (resultado) {
      setForm((p) => ({
        ...p,
        nombre: resultado.nombre || p.nombre,
        apellidos: resultado.apellidos || p.apellidos,
      }));
    }
  }, [resultado]);

  function set(field, value) {
    setForm((p) => ({ ...p, [field]: value }));
  }
  async function handleSave(e) {
    e.preventDefault();
    e.stopPropagation();
    // En modo cliente la sociedad se reconoce porque trae razón social.
    const errs = validatePropietario({ ...form, es_sociedad: !!form.nombre_sociedad?.trim() });
    if (errs.nombre_sociedad) { errs.nombre = errs.nombre_sociedad; delete errs.nombre_sociedad; }
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }
    setSaving(true);
    try {
      // 1. PATCH Persona (cedula, tipo_cedula, nombre, apellidos)
      const personaId = owner.persona?.id;
      if (personaId) {
        await apiFetch(`/api/residents/persona/${personaId}/`, {
          method: "PATCH",
          body: JSON.stringify({
            cedula: form.cedula,
            tipo_cedula: form.tipo_cedula || "",
            nombre: form.nombre,
            apellidos: form.apellidos,
            nombre_sociedad: form.nombre_sociedad || "",
          }),
        });
      }
      // 2. PATCH Propietario (teléfonos, notas)
      await apiFetch(`/api/residents/propietario/${owner.id}/`, {
        method: "PATCH",
        body: JSON.stringify({
          telefono_casa:    form.telefono_casa    || null,
          telefono_cel:     form.telefono_cel     || null,
          telefono_trabajo: form.telefono_trabajo || null,
          notas:            form.notas            || "",
        }),
      });
      // 3. PATCH Filial (ID / código del cliente)
      if (owner.filial && form.idFilial && form.idFilial !== owner.filial_data?.idFilial) {
        await apiFetch(`/api/residents/filial/${owner.filial}/`, {
          method: "PATCH",
          body: JSON.stringify({ idFilial: form.idFilial }),
        });
      }
      // 4. Email principal: se actualiza el que hay o se crea si no tenía
      if (emailObj) {
        await apiFetch(`/api/residents/emailPropietario/${emailObj.id}/`, {
          method: "PATCH",
          body: JSON.stringify({ email: form.emailPrincipal }),
        });
      } else if (form.emailPrincipal?.trim()) {
        await apiFetch(`/api/residents/emailPropietario/`, {
          method: "POST",
          body: JSON.stringify({ propietario: owner.id, email: form.emailPrincipal.trim(), principal: true }),
        });
      }
      onSaved();
    } catch (e) {
      showToast("Error al guardar: " + e.message, false);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={OVERLAY} {...overlayClose(onClose)}>
      <div style={MODAL_BOX} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ color: "#f1f5f9", margin: "0 0 16px" }}>
          <i
            className="fa-solid fa-pen-to-square"
            style={{ color: "#f97316", marginRight: 8 }}
          />
          {esCliente ? L("Editar Cliente", "Edit Client") : L("Editar Propietario", "Edit Owner")}
        </h3>

        {/* ID Cliente — solo en modo cliente */}
        {esCliente && (
          <Field label={L("ID / Código de Cliente", "Client ID / Code")} required error={errors.idFilial}>
            <Inp
              value={form.idFilial}
              placeholder="CLI-001"
              style={{ borderColor: errors.idFilial ? "#ef4444" : undefined }}
              onChange={(e) => set("idFilial", e.target.value)}
            />
          </Field>
        )}

        {/* Cédula + lookup */}
        <Field label="Cédula" required error={errors.cedula}>
          <div style={{ display: "flex", gap: 6 }}>
            <input
              type="text"
              className={`${stepsStyles["form-control"]} form-control`}
              value={form.cedula}
              placeholder="123456789"
              style={{ borderColor: errors.cedula ? "#ef4444" : undefined }}
              onChange={(e) => set("cedula", e.target.value)}
              onBlur={(e) => buscar(e.target.value)}
            />
            <button
              type="button"
              onClick={() => buscar(form.cedula)}
              disabled={estado === "buscando"}
              style={{
                padding: "0 10px",
                background: "rgba(249,115,22,0.15)",
                border: "1px solid rgba(249,115,22,0.4)",
                borderRadius: 6,
                color: "#f97316",
                cursor: "pointer",
              }}
            >
              {estado === "buscando" ? (
                <i className="fa-solid fa-circle-notch fa-spin" />
              ) : (
                <i className="fa-solid fa-magnifying-glass" />
              )}
            </button>
          </div>
        </Field>

        {/* Tipo de identificación — solo en modo cliente */}
        {esCliente && (
          <Field label={L("Tipo de identificación", "ID type")}>
            <select
              className={`${stepsStyles["form-select"]} form-select ${stepsStyles["form-control"]} form-control`}
              value={form.tipo_cedula || ""}
              onChange={(e) => set("tipo_cedula", e.target.value)}
            >
              <option value="">{L("- Seleccioná el tipo -", "- Select type -")}</option>
              <option value="FISICA">{L("Física", "Individual")}</option>
              <option value="JURIDICA">{L("Jurídica", "Company")}</option>
              <option value="DIMEX">DIMEX</option>
              <option value="NITE">NITE</option>
              <option value="PASAPORTE">{L("Pasaporte / Otro", "Passport / Other")}</option>
            </select>
          </Field>
        )}

        <div className="row g-3 mb-1">
          <div className="col-12 col-md-6">
            <Field label="Nombre" required error={errors.nombre}>
              <Inp
                value={form.nombre}
                placeholder="Nombre"
                style={{ borderColor: errors.nombre ? "#ef4444" : undefined }}
                onChange={(e) => set("nombre", e.target.value)}
              />
            </Field>
          </div>
          <div className="col-12 col-md-6">
            <Field label="Apellidos" required error={errors.apellidos}>
              <Inp
                value={form.apellidos}
                placeholder="Apellidos"
                style={{
                  borderColor: errors.apellidos ? "#ef4444" : undefined,
                }}
                onChange={(e) => set("apellidos", e.target.value)}
              />
            </Field>
          </div>
        </div>

        {/* Nombre Sociedad */}
        <div className="row g-3 mb-1">
          <div className="col-12">
            <Field label="Nombre de Sociedad">
              <Inp
                value={form.nombre_sociedad || ""}
                placeholder="Ej: Inversiones Sol S.A. (dejar vacío si es persona física)"
                onChange={(e) => set("nombre_sociedad", e.target.value)}
              />
            </Field>
          </div>
        </div>

        {/* Teléfonos */}
        <div className="row g-3 mb-1">
          <div className="col-12 col-md-4">
            <Field label="Tel. Casa">
              <Inp value={form.telefono_casa || ""} placeholder="2222-1234"
                onChange={(e) => set("telefono_casa", e.target.value)}/>
            </Field>
          </div>
          <div className="col-12 col-md-4">
            <Field label="Celular">
              <Inp value={form.telefono_cel || ""} placeholder="8888-1234"
                onChange={(e) => set("telefono_cel", e.target.value)}/>
            </Field>
          </div>
          <div className="col-12 col-md-4">
            <Field label="Tel. Trabajo">
              <Inp value={form.telefono_trabajo || ""} placeholder="2200-1234"
                onChange={(e) => set("telefono_trabajo", e.target.value)}/>
            </Field>
          </div>
        </div>

        <Field label="Email principal" required error={errors.emailPrincipal}>
          <Inp
            value={form.emailPrincipal}
            placeholder="email@ejemplo.com"
            style={{
              borderColor: errors.emailPrincipal ? "#ef4444" : undefined,
            }}
            onChange={(e) => set("emailPrincipal", e.target.value)}
          />
        </Field>

        <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            style={{
              padding: "8px 20px",
              background: "#f97316",
              border: "none",
              borderRadius: 7,
              color: "#fff",
              fontWeight: 600,
              cursor: "pointer",
              fontSize: "0.84rem",
            }}
          >
            {saving ? "Guardando…" : "Guardar cambios"}
          </button>
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: "8px 16px",
              background: "rgba(55,65,81,0.4)",
              border: "1px solid var(--border-card)",
              borderRadius: 7,
              color: "var(--text-secondary)",
              cursor: "pointer",
            }}
          >
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// TABLE COMPONENTS (Owners, CoOwners, Authorized, Filiales)
// ═══════════════════════════════════════════════════════════════════════════════

function ActionButtons({ onEdit, onDelete }) {
  return (
    <div
      className="d-flex align-items-center justify-content-start"
      style={{ gap: 8 }}
    >
      <button
        type="button"
        onClick={onEdit}
        style={{
          background: "rgba(249,115,22,0.1)",
          border: "1px solid rgba(249,115,22,0.3)",
          borderRadius: 6,
          padding: "4px 10px",
          color: "#f97316",
          cursor: "pointer",
        }}
        title="Editar"
      >
        <i className="fa-solid fa-square-pen" />
      </button>
      <button
        type="button"
        onClick={onDelete}
        style={{
          background: "rgba(239,68,68,0.1)",
          border: "1px solid rgba(239,68,68,0.25)",
          borderRadius: 6,
          padding: "4px 10px",
          color: "#f87171",
          cursor: "pointer",
        }}
        title="Eliminar"
      >
        <i className="fa-solid fa-trash" />
      </button>
    </div>
  );
}

function TableShell({
  columns,
  children,
  page,
  totalPages,
  onPrev,
  onNext,
  total,
}) {
  return (
    <>

      <div
        className={`${styles["tab-pane"]} active ${styles["table-responsive"]}`}
      >
        <table>
          <thead>
            <tr>
              {columns.map((c) => (
                <th key={c}>{c}</th>
              ))}
            </tr>
          </thead>
          <tbody>{children}</tbody>
        </table>
      </div>
      <div className={styles["table-footer"]}>
        <p style={{color:"var(--text-secondary)", fontSize:"0.9rem"}}>
          <>
            <span>{total}</span> resultado(s) · Página <span>{page}</span> de{" "}
            <span>{Math.max(1, totalPages)}</span>
          </>
        </p>
        <div className={`${styles["pagination-buttons"]} d-flex gap-2`}>
          <button
            className={`btn btn-sm ${styles["btn-prev"]} d-flex gap-2 align-items-center`}
            disabled={page <= 1}
            onClick={onPrev}
          >
            <i className="fa-solid fa-chevron-left" />
            Anterior
          </button>
          <button
            className={`btn btn-sm ${styles["btn-next"]} d-flex gap-2 align-items-center`}
            disabled={page >= totalPages}
            onClick={onNext}
          >
            Siguiente
            <i className="fa-solid fa-chevron-right" />
          </button>
        </div>
      </div>
    </>
  );
}

// ── useTableData: shared fetch + search + sort logic ─────────────────────────
function useTableData(endpoint, search, sortField) {
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [refresh, setRefresh] = useState(0);

  const reload = useCallback(() => setRefresh((r) => r + 1), []);

  useEffect(() => {
    setPage(1);
  }, [search, sortField]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = new URLSearchParams({ page, page_size: 8 });
    if (search) params.set("search", search);
    if (sortField) params.set("ordering", sortField);
    apiFetch(`${endpoint}?${params}`)
      .then((r) => r.json())
      .then((d) => {
        if (!cancelled) {
          setData(d.results ?? d);
          setTotal(d.count ?? d.results?.length ?? d.length);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [endpoint, page, search, sortField, refresh]);

  const totalPages = Math.ceil(total / 8);
  return { data, total, page, totalPages, loading, reload, setPage };
}

// ── Helpers de presentación ───────────────────────────────────────────────────
const nombreDe = (persona) =>
  persona?.nombre_completo ||
  persona?.nombre_sociedad ||
  `${persona?.nombre || ""} ${persona?.apellidos || ""}`.trim() ||
  "-";

const primerTelefono = (r) =>
  [r?.telefono_cel, r?.telefono_casa, r?.telefono_trabajo, r?.telefono]
    .find((t) => t && String(t).trim() !== "" && String(t).trim() !== "-");

const placasDe = (r) => (r?.vehiculos || []).map((v) => v.placa).join(", ");

const Vacio = () => <span style={{ color: "#4b5563" }}>-</span>;

function FilaCargando() {
  return (
    <tr>
      <td colSpan={5} style={{ textAlign: "center", padding: 24, color: "var(--text-secondary)" }}>
        <i className="fa-solid fa-circle-notch fa-spin" style={{ marginRight: 6, color: "#f97316" }} />
        Cargando…
      </td>
    </tr>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// ELIMINAR FILIAL / PROPIETARIO
// Borra la filial completa (propietario, copropietarios y autorizados). Antes
// pregunta al backend qué se borraría y si la filial tiene historial, que lo
// impide (facturas, recibos, movimientos, asientos...).
// ═══════════════════════════════════════════════════════════════════════════════
function DeleteResidentModal({ filialId, propietarioId = null, onClose, onDeleted }) {
  const navigate = useNavigate();
  const [resumen, setResumen] = useState(null);
  const [error, setError] = useState("");
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    let cancel = false;
    apiFetch(`/api/residents/filial/${filialId}/resumen-eliminacion/`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error("No se pudo revisar la filial."))))
      .then((d) => !cancel && setResumen(d))
      .catch((e) => !cancel && setError(e.message));
    return () => { cancel = true; };
  }, [filialId]);

  async function handleDelete() {
    setDeleting(true);
    try {
      const url = propietarioId
        ? `/api/residents/propietario/${propietarioId}/`
        : `/api/residents/filial/${filialId}/`;
      const res = await apiFetch(url, { method: "DELETE" });
      if (res.status === 204) {
        onDeleted();
        return;
      }
      const body = await res.json().catch(() => ({}));
      setError(body.detail || `Error HTTP ${res.status}`);
    } catch (e) {
      setError(e.message);
    } finally {
      setDeleting(false);
    }
  }

  const bloqueada = resumen?.historial?.length > 0;
  // Si la filial tiene otro propietario, solo se quita este y la filial queda.
  const soloPropietario = propietarioId && resumen?.propietarios > 1;

  const btnBase = {
    padding: "9px 20px", borderRadius: 7, cursor: "pointer", fontSize: "0.84rem",
  };

  return (
    <div style={OVERLAY} {...overlayClose(onClose)}>
      <div style={{ ...MODAL_BOX, width: "min(500px,94vw)" }} onClick={(e) => e.stopPropagation()}>
        <div style={{ textAlign: "center", marginBottom: 14 }}>
          <div style={{ fontSize: "2.3rem", marginBottom: 6 }}>{bloqueada ? "🔒" : "🗑️"}</div>
          <h3 style={{ color: "var(--text-primary)", margin: 0 }}>
            {bloqueada
              ? `La filial ${resumen.filial} no se puede eliminar`
              : soloPropietario
                ? "¿Quitar este propietario?"
                : `¿Eliminar la filial ${resumen?.filial || ""}?`}
          </h3>
        </div>

        {!resumen && !error && (
          <p style={{ textAlign: "center", color: "var(--text-muted)" }}>
            <i className="fa-solid fa-circle-notch fa-spin" style={{ marginRight: 6, color: "#f97316" }} />
            Revisando la filial…
          </p>
        )}

        {resumen && bloqueada && (
          <>
            <p style={{ color: "var(--text-secondary)", fontSize: "0.87rem", lineHeight: 1.6 }}>
              Tiene historial y eliminarla borraría también toda esa información contable:
            </p>
            <ul style={{ color: "var(--text-primary)", fontSize: "0.86rem", margin: "0 0 12px 18px" }}>
              {resumen.historial.map((h) => (
                <li key={h.etiqueta}>{h.cantidad} {h.etiqueta}</li>
              ))}
            </ul>
            <p style={{ color: "var(--text-secondary)", fontSize: "0.85rem", lineHeight: 1.6 }}>
              Si la propiedad cambió de dueño, usá <strong>Editar</strong> para cambiar el
              propietario: las facturas anteriores conservan el nombre del dueño anterior.
            </p>
          </>
        )}

        {resumen && !bloqueada && (
          <div style={{ color: "var(--text-secondary)", fontSize: "0.87rem", lineHeight: 1.7 }}>
            {soloPropietario ? (
              <p>
                Se quitará a <strong style={{ color: "#f87171" }}>{resumen.propietario}</strong>.
                La filial tiene otro propietario, así que se conserva con sus copropietarios y
                autorizados.
              </p>
            ) : (
              <>
                <p style={{ marginBottom: 6 }}>Se eliminará permanentemente:</p>
                <ul style={{ color: "var(--text-primary)", margin: "0 0 10px 18px" }}>
                  <li>La filial <strong>{resumen.filial}</strong></li>
                  {resumen.propietario && <li>El propietario {resumen.propietario}</li>}
                  <li>{resumen.copropietarios} copropietario(s)</li>
                  <li>{resumen.autorizados} autorizado(s)</li>
                </ul>
                <p style={{ color: "#f87171" }}>Esta acción no se puede deshacer.</p>
              </>
            )}
          </div>
        )}

        {error && (
          <p style={{ color: "#f87171", fontSize: "0.84rem", textAlign: "center" }}>{error}</p>
        )}

        <div style={{ display: "flex", gap: 8, justifyContent: "center", marginTop: 12 }}>
          <button type="button" onClick={onClose}
            style={{ ...btnBase, background: "rgba(55,65,81,0.4)", border: "1px solid var(--border-card)",
              color: "var(--text-secondary)" }}>
            {bloqueada ? "Cerrar" : "Cancelar"}
          </button>
          {bloqueada ? (
            <button type="button" onClick={() => navigate(`/residentes/editar/${filialId}`)}
              style={{ ...btnBase, background: "#f97316", border: "none", color: "#fff", fontWeight: 600 }}>
              Editar filial
            </button>
          ) : (
            <button type="button" onClick={handleDelete} disabled={!resumen || deleting}
              style={{ ...btnBase, background: "rgba(239,68,68,0.15)", border: "1px solid rgba(239,68,68,0.4)",
                color: "#f87171", fontWeight: 700, opacity: !resumen || deleting ? 0.6 : 1 }}>
              {deleting ? "Eliminando…" : soloPropietario ? "Sí, quitar" : "Sí, eliminar todo"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Owners Table ──────────────────────────────────────────────────────────────
function OwnersTable({ search, sortField }) {
  const esCliente = useEsCliente();
  const L = useLang();
  const navigate = useNavigate();
  const { data, total, page, totalPages, loading, reload, setPage } =
    useTableData("/api/residents/propietario/", search, sortField);

  const [editRecord, setEditRecord] = useState(null);
  const [deleteRecord, setDeleteRecord] = useState(null);

  function handleEdit(o) {
    // Modo condominio: el asistente completo de la filial. Cliente: su ventana.
    if (esCliente) setEditRecord(o);
    else navigate(`/residentes/editar/${o.filial}`);
  }

  return (
    <>
      <TableShell
        columns={[esCliente ? L("ID Cliente", "Client ID") : "N.Filial", L("Nombre", "Name"), "Email", L("Teléfono", "Phone"), L("Acciones", "Actions")]}
        page={page}
        totalPages={totalPages}
        total={total}
        onPrev={() => setPage((p) => p - 1)}
        onNext={() => setPage((p) => p + 1)}
      >
        {loading ? (
          <FilaCargando />
        ) : (
          data.map((o) => (
            <tr key={o.id}>
              <td style={{ fontWeight: 600 }}>{o.filial_data?.idFilial || "-"}</td>
              <td>{nombreDe(o.persona)}</td>
              <td>
                {o.email_principal || (o.sin_correo ? (
                  <span style={{ fontSize: "0.78rem", color: "var(--text-muted)", border: "1px dashed var(--border-card)",
                    borderRadius: 999, padding: "1px 8px" }}>
                    Sin correo
                  </span>
                ) : <Vacio />)}
              </td>
              <td>{primerTelefono(o) || <Vacio />}</td>
              <td>
                <ActionButtons
                  onEdit={() => handleEdit(o)}
                  onDelete={() => setDeleteRecord(o)}
                />
              </td>
            </tr>
          ))
        )}
      </TableShell>

      {editRecord && (
        <EditOwnerModal
          owner={editRecord}
          onClose={() => setEditRecord(null)}
          onSaved={() => {
            setEditRecord(null);
            reload();
          }}
        />
      )}
      {deleteRecord && (
        <DeleteResidentModal
          filialId={deleteRecord.filial}
          propietarioId={esCliente ? null : deleteRecord.id}
          onClose={() => setDeleteRecord(null)}
          onDeleted={() => {
            setDeleteRecord(null);
            reload();
          }}
        />
      )}
    </>
  );
}

// ── Personas de la filial (copropietarios / autorizados) ─────────────────────
// Editar abre el asistente de su filial; eliminar las quita directo.
function PersonasTable({ endpoint, search, sortField, columnaExtra }) {
  const navigate = useNavigate();
  const { data, total, page, totalPages, loading, reload, setPage } =
    useTableData(endpoint, search, sortField);

  const [deleteRecord, setDeleteRecord] = useState(null);
  const [deleting, setDeleting] = useState(false);

  async function handleDelete() {
    setDeleting(true);
    try {
      const res = await apiFetch(`${endpoint}${deleteRecord.id}/`, { method: "DELETE" });
      if (!res.ok && res.status !== 204) throw new Error(`HTTP ${res.status}`);
      setDeleteRecord(null);
      reload();
    } catch (e) {
      showToast("Error al eliminar: " + e.message, false);
    } finally {
      setDeleting(false);
    }
  }

  return (
    <>
      <TableShell
        columns={["N.Filial", "Nombre", columnaExtra.titulo, "Placas", "Acciones"]}
        page={page}
        totalPages={totalPages}
        total={total}
        onPrev={() => setPage((p) => p - 1)}
        onNext={() => setPage((p) => p + 1)}
      >
        {loading ? (
          <FilaCargando />
        ) : (
          data.map((r) => (
            <tr key={r.id}>
              <td style={{ fontWeight: 600 }}>{r.filial_data?.idFilial || "-"}</td>
              <td>{nombreDe(r.persona)}</td>
              <td>{columnaExtra.valor(r) || <Vacio />}</td>
              <td>{placasDe(r) || <Vacio />}</td>
              <td>
                <ActionButtons
                  onEdit={() => navigate(`/residentes/editar/${r.filial}`)}
                  onDelete={() => setDeleteRecord(r)}
                />
              </td>
            </tr>
          ))
        )}
      </TableShell>
      <ConfirmModal
        open={!!deleteRecord}
        loading={deleting}
        name={deleteRecord ? nombreDe(deleteRecord.persona) : ""}
        onConfirm={handleDelete}
        onCancel={() => setDeleteRecord(null)}
      />
    </>
  );
}

function CoOwnersTable({ search, sortField }) {
  return (
    <PersonasTable
      endpoint="/api/residents/copropietario/"
      search={search}
      sortField={sortField}
      columnaExtra={{ titulo: "Teléfono", valor: primerTelefono }}
    />
  );
}

function AuthorizedTable({ search, sortField }) {
  return (
    <PersonasTable
      endpoint="/api/residents/autorizado/"
      search={search}
      sortField={sortField}
      columnaExtra={{ titulo: "Relación", valor: (r) => r.tipo }}
    />
  );
}

// ── Filiales Table ────────────────────────────────────────────────────────────
function FilialesTable({ search, sortField }) {
  const navigate = useNavigate();
  const { data, total, page, totalPages, loading, reload, setPage } =
    useTableData("/api/residents/filial/", search, sortField);

  const [deleteRecord, setDeleteRecord] = useState(null);

  return (
    <>
      <TableShell
        columns={["N.Filial", "N.Plano", "N.Folio", "Tipo", "Acciones"]}
        page={page}
        totalPages={totalPages}
        total={total}
        onPrev={() => setPage((p) => p - 1)}
        onNext={() => setPage((p) => p + 1)}
      >
        {loading ? (
          <FilaCargando />
        ) : (
          data.map((f) => (
            <tr key={f.id}>
              <td style={{ fontWeight: 600 }}>{f.idFilial}</td>
              <td>{f.numPlano || <Vacio />}</td>
              <td>{f.numFolio || <Vacio />}</td>
              <td>{f.tipo_propiedad || <Vacio />}</td>
              <td>
                <ActionButtons
                  onEdit={() => navigate(`/residentes/editar/${f.id}`)}
                  onDelete={() => setDeleteRecord(f)}
                />
              </td>
            </tr>
          ))
        )}
      </TableShell>
      {deleteRecord && (
        <DeleteResidentModal
          filialId={deleteRecord.id}
          onClose={() => setDeleteRecord(null)}
          onDeleted={() => {
            setDeleteRecord(null);
            reload();
          }}
        />
      )}
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN PAGE
// ═══════════════════════════════════════════════════════════════════════════════
const TABS = ["Propietarios", "Copropietarios", "Autorizados", "Filiales"];

const SORT_OPTIONS_PERSONA = [
  { label: "N.Filial A→Z", value: "filial__idFilial" },
  { label: "N.Filial Z→A", value: "-filial__idFilial" },
  { label: "Apellidos A→Z", value: "persona__apellidos" },
  { label: "Apellidos Z→A", value: "-persona__apellidos" },
];
const SORT_FILIAL_OPTIONS = [
  { label: "ID Filial A→Z", value: "idFilial" },
  { label: "ID Filial Z→A", value: "-idFilial" },
];

export default function ListResidents() {
  const esCliente = useEsCliente();
  const L = useLang();
  const [activeTab, setActiveTab] = useState(0);
  const [search, setSearch] = useState("");
  const [sortField, setSortField] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);
  const [importErrores, setImportErrores] = useState([]); // filas con error del último import

  // En modo cliente solo aplica la pestaña de "Clientes" (los propietarios).
  const tabs = esCliente
    ? [L("Clientes", "Clients")]
    : (L("es","en") === "en"
        ? ["Owners", "Co-owners", "Authorized", "Units"]
        : TABS);

  const sortOptions =
    activeTab === 3 ? SORT_FILIAL_OPTIONS :
    SORT_OPTIONS_PERSONA;

  function handleTabChange(idx) {
    setActiveTab(idx);
    setSearch("");
    setSortField("");
  }

  return (
    <>
      <span className={styles.inactive}>
        <Link to="/">
          <span className={styles.inactive}>{L("Inicio", "Home")} </span>
        </Link>
        / <span className={styles.active}>{esCliente ? L("Clientes", "Clients") : L("Residentes", "Residents")}</span>
      </span>

      <RelacionesAlert campos={["rel_cuenta_cxc_filial","rel_cuenta_adelantado_filial"]}/>

      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2" style={{ marginBottom: 6 }}>
        <div>
          <h2 className="mt-2 me-2 p-0 title">{esCliente ? L("Gestión de Clientes", "Client Management") : L("Gestión de Residentes", "Resident Management")}</h2>
          <p className="mb-3" style={{color:"var(--text-muted)"}}>
            {esCliente
              ? L("Administra los clientes de tu organización", "Manage your organization's clients")
              : L("Administra los residentes de tu condominio", "Manage your condominium's residents")}
          </p>
        </div>
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <ImportExportBar
            plantillaUrl="/api/residents/filial/plantilla-excel/"
            plantillaFilename={esCliente ? "plantilla_clientes.xlsx" : "plantilla_filiales.xlsx"}
            importarUrl="/api/residents/filial/importar-excel/"
            exportarUrl={esCliente ? undefined : "/api/residents/filial/exportar-excel/"}
            exportarFilename="residentes.xlsx"
            onResult={(res) => {
              setImportErrores(res?.errores || []);
              if (res) {
                const n = res.errores?.length || 0;
                showToast(n ? `${res.msg} ${n} fila(s) con errores o advertencias.` : res.msg, res.ok && !n);
                if (res.ok) setRefreshKey((k) => k + 1);
              }
            }}
          />
          <Link
            to="/residentes/agregar"
            className={`btn ${styles["btn-add-resident"]} d-flex gap-2 align-items-center`}
          >
            <i className="fa-solid fa-plus" />
            {esCliente ? L("Agregar Cliente", "Add Client") : L("Agregar Residente", "Add Resident")}
          </Link>
        </div>
      </div>

      {importErrores.length > 0 && (
        <div style={{ margin: "0 0 12px", padding: "12px 16px", borderRadius: 8,
          background: "rgba(239,68,68,0.06)", border: "1px solid rgba(239,68,68,0.25)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
            <strong style={{ color: "#f87171", fontSize: "0.86rem" }}>
              <i className="fa-solid fa-triangle-exclamation" style={{ marginRight: 6, color: "inherit" }} />
              {importErrores.length} fila(s) del archivo con errores o advertencias
            </strong>
            <button type="button" onClick={() => setImportErrores([])}
              style={{ background: "none", border: "none", color: "var(--text-muted)", cursor: "pointer" }}
              title="Cerrar">
              <i className="fa-solid fa-xmark" />
            </button>
          </div>
          <div style={{ maxHeight: 180, overflowY: "auto", fontSize: "0.82rem" }}>
            {importErrores.map((e, i) => (
              <div key={i} style={{ padding: "3px 0", color: e.advertencia ? "#fbbf24" : "var(--text-secondary)" }}>
                <strong>Fila {e.fila}:</strong> {(e.errores || []).join(" · ")}
                {e.advertencia && " (se importó el resto de la fila)"}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className={styles["table-container"]}>
        {/* Search + sort bar */}
        <TableToolbar
          search={search}
          onSearch={setSearch}
          searchPlaceholder={
            esCliente
              ? L("Buscar por nombre o cliente…", "Search by name or client…")
              : activeTab === 3
                ? L("Buscar por N. Filial…", "Search by Unit No…")
                : L("Buscar por nombre o filial…", "Search by name or unit…")
          }
          sort={
            <ToolbarSelect
              value={sortField}
              active={!!sortField}
              onChange={(e) => setSortField(e.target.value)}
              title="Ordenar"
            >
              <option value="">Ordenar…</option>
              {sortOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </ToolbarSelect>
          }
        />

        {/* Tabs */}
        <div className={styles["tabs-wrapper"]}>
          <div className={styles["tabs-nav"]}>
            {tabs.map((tab, idx) => (
              <button
                key={idx}
                className={`${styles["tab-btn"]} ${activeTab === idx ? styles.active : ""}`}
                onClick={() => handleTabChange(idx)}
              >
                {tab}
              </button>
            ))}
          </div>

          <div className="tabs-body">
            {activeTab === 0 && (
              <OwnersTable key={refreshKey} search={search} sortField={sortField} />
            )}
            {activeTab === 1 && (
              <CoOwnersTable key={refreshKey} search={search} sortField={sortField} />
            )}
            {activeTab === 2 && (
              <AuthorizedTable key={refreshKey} search={search} sortField={sortField} />
            )}
            {activeTab === 3 && (
              <FilialesTable key={refreshKey} search={search} sortField={sortField} />
            )}
          </div>
        </div>
      </div>
    </>
  );
}
